import React, { useState, useEffect, useCallback, useRef } from 'react';
import AppSpinner from "../Spinner/AppSpinner";
import { Modal, Toast, ToastContainer } from 'react-bootstrap';
import { BASE_URL } from '../../constants';
import { apiFetch } from "../../utils/apiFetch";
import LectureSlides from '../LectureSlides/LectureSlides';
import LectureWorks from '../LectureWorks/LectureWorks';
import LectureTests from '../LectureTests/LectureTests.jsx';
import PageInfoModal from '../PageInfoModal/PageInfoModal';

const LECTURE_DETAIL_INFO_ITEMS = [
  { icon: 'file-earmark-slides', text: '"Slaydlar" tabında bu dərsin təqdimat slaydlarına baxa bilərsiniz.' },
  { icon: 'file-earmark-code', text: '"Tapşırıqlar" tabında tələbələrə verilən praktik tapşırıqları görə bilərsiniz.' },
  { icon: 'patch-question', text: '"Testlər" tabında bu dərsə aid quiz testlərini görə bilərsiniz.' },
  { icon: 'plus-circle', text: 'Hər tabda "Əlavə et" düyməsi ilə həmin kateqoriyaya yeni məzmun əlavə edə bilərsiniz.' },
  { icon: 'trash', text: 'Mövcud slayd, tapşırıq və ya testlər üzərindəki sil düyməsi ilə onları silə bilərsiniz.' },
  { icon: 'arrow-clockwise', text: 'Yenilə (bənövşəyi) düyməsi ilə dərs məlumatlarını yenidən yükləyə bilərsiniz.' },
];

const parseAzName = (raw) => {
  if (!raw) return '';
  const match = raw.match(/Az:::\s*(.*?)\s*(?:Rus:::|Eng:::|$)/);
  return match ? match[1].trim() : raw;
};

const LectureDetail = ({ show, onHide, lectureId }) => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [toasts, setToasts] = useState([]);
  const toastTimers = useRef([]);

  const showToast = useCallback((message, type = 'success') => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type }]);
    const timer = setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 2000);
    toastTimers.current.push(timer);
  }, []);

  useEffect(() => {
    const timers = toastTimers.current;
    return () => { timers.forEach(clearTimeout); };
  }, []);

  const fetchDetails = useCallback(async () => {
    if (!lectureId) return;
    setLoading(true);
    try {
      const response = await apiFetch(`${BASE_URL}/lectures/${lectureId}/details`);
      const data = await response.json();
      setDetails(data);
    } catch (err) {
      console.error("Dərs məlumatı alınarkən xəta:", err);
    } finally {
      setLoading(false);
    }
  }, [lectureId]);

  useEffect(() => {
    if (show && lectureId) fetchDetails();
  }, [show, lectureId, fetchDetails]);

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        centered
        dialogClassName="lecture-detail-modal"
      >
        <style>{`
          .lecture-detail-modal { max-width: 88vw; width: 88vw; }
          .lecture-detail-modal .modal-content { height: 88vh; display: flex; flex-direction: column; border-radius: 10px; overflow: hidden; }
          .lecture-detail-modal .modal-body { flex: 1; overflow: hidden; padding: 0; }

          @media (max-width: 1300px) {
            .lecture-detail-modal { max-width: 96vw; width: 96vw; }
            .lecture-detail-modal .modal-body { zoom: 0.9; height: calc(100% / 0.9); }
          }
          @media (max-width: 900px) {
            .lecture-detail-modal { max-width: 100vw; width: 100vw; }
            .lecture-detail-modal .modal-content { height: auto; max-height: 96vh; }
            .lecture-detail-modal .modal-body { zoom: 1; height: auto; overflow-y: auto; }
            .ld-col-grid {
              grid-template-columns: 1fr !important;
              height: auto !important;
              overflow: visible !important;
            }
            .ld-col-grid > div {
              border-right: none !important;
              border-bottom: 1px solid #e5e7eb;
              height: 380px;
              min-height: 380px;
              max-height: 380px;
            }
            .ld-col-grid > div:last-child { border-bottom: none; }
          }
        `}</style>

        {/* ---- HEADER ---- */}
        <Modal.Header style={{
          background: '#f3f4f6',
          borderBottom: '1px solid #e5e7eb',
          padding: '10px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <PageInfoModal title="Dərs Detalı" items={LECTURE_DETAIL_INFO_ITEMS} />
            {loading ? (
              <AppSpinner inline />
            ) : (
              <>
                <span style={{ fontWeight: 700, color: '#374151', fontSize: 14 }}>
                  {parseAzName(details?.name) || '...'}
                </span>
                {details?.creatorUsername && (
                  <>
                    <span style={{ color: '#d1d5db' }}>|</span>
                    <span style={{
                      background: '#f3e8ff', color: '#7c3aed',
                      padding: '2px 8px', borderRadius: 4,
                      fontWeight: 600, fontSize: 11,
                      border: '1px solid #e9d5ff'
                    }}>
                      {details.creatorUsername}
                    </span>
                  </>
                )}
              </>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              onClick={fetchDetails}
              title="Yenilə"
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                background: '#2563eb', color: '#fff', border: 'none',
                borderRadius: 6, padding: '6px 8px', cursor: 'pointer'
              }}
            >
              <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: 14, height: 14 }}>
                <path d="M4 4a7 7 0 0 1 12 4.9M4 10a7 7 0 0 0 12 4.9M16 4v4h-4M4 16v-4h4"
                  stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <button
              type="button"
              className="btn-close"
              onClick={onHide}
              aria-label="Close"
              style={{ margin: 0 }}
            />
          </div>
        </Modal.Header>

        {/* ---- DESCRIPTION BAR ---- */}
        {!loading && details?.description && (
          <div style={{
            background: '#fff',
            borderBottom: '1px solid #e5e7eb',
            padding: '6px 16px',
            fontSize: 12,
            color: '#6b7280',
            flexShrink: 0
          }}>
            {details.description}
          </div>
        )}

        {/* ---- BODY ---- */}
        <Modal.Body>
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <AppSpinner text="Məlumatlar gətirilir..." />
            </div>
          ) : (
            <div className="ld-col-grid" style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              height: '100%',
              overflow: 'hidden'
            }}>
              <div style={{ borderRight: '1px solid #e5e7eb', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <LectureSlides
                  lectureId={lectureId}
                  initialSlides={details?.slides || []}
                  onRefresh={fetchDetails}
                  onShowToast={showToast}
                />
              </div>
              <div style={{ borderRight: '1px solid #e5e7eb', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <LectureWorks
                  lectureId={lectureId}
                  initialWorks={details?.tasks || []}
                  onRefresh={fetchDetails}
                  onShowToast={showToast}
                />
              </div>
              <div style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <LectureTests
                  lectureId={lectureId}
                  initialTests={details?.quizzes || []}
                  onRefresh={fetchDetails}
                  onShowToast={showToast}
                />
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* ---- TOAST ---- */}
      <ToastContainer position="bottom-end" className="p-3" style={{ zIndex: 99999 }}>
        {toasts.map(toast => (
          <Toast key={toast.id} bg={toast.type === 'success' ? 'success' : 'danger'} show autohide delay={2000}>
            <Toast.Header closeButton={false}>
              <strong className="me-auto">Sistem Mesajı</strong>
            </Toast.Header>
            <Toast.Body className="text-white">{toast.message}</Toast.Body>
          </Toast>
        ))}
      </ToastContainer>
    </>
  );
};

export default LectureDetail;
